import type { components } from '@joyful/openapi/types.gen'
import { z } from 'zod'

import type { JoyfulApiClient } from './api'
import { getActiveOrgSlugCookie } from './activeOrgSlugCookie'
import type { BrowserErrorApp } from './appContext'

const BrowserErrorKindSchema = z.enum([
  'unhandled_error',
  'unhandled_rejection',
  'react_error_boundary',
  'analysis_generation_failed',
])
export const ErrorLikeSchema = z.object({
  message: z.string(),
  name: z.string().optional(),
  stack: z.string().optional(),
})
const WindowErrorEventSchema = z.object({
  error: ErrorLikeSchema.optional(),
  message: z.string(),
})
const RejectionReasonSchema = z.union([z.string(), ErrorLikeSchema])
const PromiseRejectionEventSchema = z.object({
  reason: RejectionReasonSchema.optional(),
})
const UuidPathSegmentSchema = z
  .string()
  .regex(
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
  )

const MESSAGE_MAX_LENGTH = 1024
const STACK_MAX_LENGTH = 4096
const ROUTE_TEMPLATE_MAX_LENGTH = 256
const DEDUPE_WINDOW_MS = 60_000
const FLUSH_DELAY_MS = 2_000
const MAX_BATCH_SIZE = 20
const MAX_QUEUED_EVENTS = 50
const URL_IN_TEXT =
  /(?:https?|chrome-extension|moz-extension|file):\/\/\S+|www\.\S+/g

export interface BrowserErrorEventInput {
  errorName?: string
  kind: z.infer<typeof BrowserErrorKindSchema>
  message: string
  stack?: string
}

export interface BrowserErrorReporter {
  flush: () => Promise<void>
  report: (event: BrowserErrorEventInput) => void
  stop: () => void
}

type BrowserErrorEventPayload = components['schemas']['BrowserErrorEvent']

interface QueuedBrowserError {
  event: BrowserErrorEventPayload
  orgSlug: string | null
}

interface BrowserErrorReporterOptions {
  app: BrowserErrorApp
  flushDelayMs?: number
  getOrgSlug?: () => string | null
  getRouteTemplate?: () => string | null
  now?: () => number
  release?: string
  schedule?: (callback: () => void, delayMs: number) => number
  send: (payload: {
    events: Array<BrowserErrorEventPayload>
    orgSlug: string
  }) => Promise<void>
  unschedule?: (handle: number) => void
}

let activeReporter: BrowserErrorReporter | null = null
let hasAttachedWindowListeners = false
let routeOrgSlug: string | null = null

export function setBrowserErrorOrgSlug(orgSlug: string | null) {
  routeOrgSlug = orgSlug
}

export function reportBrowserError(event: BrowserErrorEventInput) {
  try {
    activeReporter?.report(event)
  } catch {
    return
  }
}

export function startBrowserErrorReporter(options: {
  api: JoyfulApiClient
  app: BrowserErrorApp
  release?: string
}) {
  try {
    if (activeReporter) {
      return
    }

    activeReporter = createBrowserErrorReporter({
      app: options.app,
      release: options.release,
      send: createDefaultSend(options.api),
    })
    attachWindowListeners()
  } catch {
    return
  }
}

export function createBrowserErrorReporter(
  options: BrowserErrorReporterOptions,
) {
  const now = options.now ?? Date.now
  const schedule = options.schedule ?? defaultSchedule
  const unschedule = options.unschedule ?? defaultUnschedule
  const flushDelayMs = options.flushDelayMs ?? FLUSH_DELAY_MS
  const getOrgSlug = options.getOrgSlug ?? defaultOrgSlug
  const getRouteTemplate =
    options.getRouteTemplate ??
    (() => {
      return defaultRouteTemplate(options.app)
    })
  const send = options.send
  const recentlySentMessages = new Map<string, number>()
  const queue: Array<QueuedBrowserError> = []
  let flushHandle: number | undefined
  let isStopped = false

  const reporter: BrowserErrorReporter = {
    flush: async () => {
      if (flushHandle !== undefined) {
        unschedule(flushHandle)
        flushHandle = undefined
      }

      await flushQueue()
    },

    report: (event) => {
      if (isStopped) {
        return
      }

      try {
        const message = truncate(stripUrls(event.message), MESSAGE_MAX_LENGTH)
        if (message === '') {
          return
        }

        const currentTime = now()
        pruneDedupe(recentlySentMessages, currentTime)
        const lastSentAt = recentlySentMessages.get(message)
        if (
          lastSentAt !== undefined &&
          currentTime - lastSentAt < DEDUPE_WINDOW_MS
        ) {
          return
        }

        recentlySentMessages.set(message, currentTime)
        enqueue({
          event: {
            app: options.app,
            error_name: truncateOptional(event.errorName, 128),
            kind: event.kind,
            message,
            release: truncateOptional(options.release, 128),
            route_template: sanitizeRouteTemplate(getRouteTemplate()),
            stack: truncateOptional(
              event.stack ? stripUrls(event.stack) : null,
              STACK_MAX_LENGTH,
            ),
          },
          orgSlug: getOrgSlug(),
        })
        scheduleFlush()
      } catch {
        return
      }
    },

    stop: () => {
      isStopped = true
      if (flushHandle !== undefined) {
        unschedule(flushHandle)
        flushHandle = undefined
      }

      queue.length = 0
    },
  }

  function enqueue(queuedEvent: QueuedBrowserError) {
    queue.push(queuedEvent)
    if (queue.length > MAX_QUEUED_EVENTS) {
      queue.splice(0, queue.length - MAX_QUEUED_EVENTS)
    }
  }

  function scheduleFlush() {
    if (flushHandle !== undefined) {
      return
    }

    flushHandle = schedule(() => {
      flushHandle = undefined
      void flushQueue()
    }, flushDelayMs)
  }

  async function flushQueue() {
    const currentOrgSlug = getOrgSlug()
    if (currentOrgSlug) {
      for (const queuedEvent of queue) {
        if (!queuedEvent.orgSlug) {
          queuedEvent.orgSlug = currentOrgSlug
        }
      }
    }

    const orgSlug = queue.find((queuedEvent) => {
      return queuedEvent.orgSlug
    })?.orgSlug
    if (!orgSlug) {
      if (queue.length > 0) {
        scheduleFlush()
      }

      return
    }

    const eventsToSend: Array<QueuedBrowserError> = []
    const remainingEvents: Array<QueuedBrowserError> = []
    for (const queuedEvent of queue) {
      const belongsInBatch =
        queuedEvent.orgSlug === orgSlug && eventsToSend.length < MAX_BATCH_SIZE
      if (belongsInBatch) {
        eventsToSend.push(queuedEvent)
      } else {
        remainingEvents.push(queuedEvent)
      }
    }

    queue.length = 0
    queue.push(...remainingEvents)
    try {
      await send({
        events: eventsToSend.map((queuedEvent) => {
          return queuedEvent.event
        }),
        orgSlug,
      })
    } catch {
      queue.unshift(...eventsToSend)
      scheduleFlush()
      return
    }

    if (queue.length > 0) {
      scheduleFlush()
    }
  }

  return reporter
}

function attachWindowListeners() {
  if (hasAttachedWindowListeners) {
    return
  }

  hasAttachedWindowListeners = true
  globalThis.addEventListener('error', onWindowError)
  globalThis.addEventListener('unhandledrejection', onUnhandledRejection)
}

function onWindowError(event: Event) {
  const parsed = WindowErrorEventSchema.safeParse(event)
  if (!parsed.success) {
    return
  }

  reportBrowserError({
    errorName: parsed.data.error?.name,
    kind: 'unhandled_error',
    message: parsed.data.message,
    stack: parsed.data.error?.stack,
  })
}

function onUnhandledRejection(event: Event) {
  const parsed = PromiseRejectionEventSchema.safeParse(event)
  if (!parsed.success) {
    reportBrowserError({
      kind: 'unhandled_rejection',
      message: 'unhandled rejection',
    })
    return
  }

  const reason = parsed.data.reason
  const reasonAsString = z.string().safeParse(reason)
  if (reasonAsString.success) {
    reportBrowserError({
      kind: 'unhandled_rejection',
      message: reasonAsString.data,
    })
    return
  }

  const reasonAsError = ErrorLikeSchema.safeParse(reason)
  if (reasonAsError.success) {
    reportBrowserError({
      errorName: reasonAsError.data.name,
      kind: 'unhandled_rejection',
      message: reasonAsError.data.message,
      stack: reasonAsError.data.stack,
    })
    return
  }

  reportBrowserError({
    kind: 'unhandled_rejection',
    message: 'unhandled rejection',
  })
}

function createDefaultSend(api: JoyfulApiClient) {
  return async ({
    events,
    orgSlug,
  }: {
    events: Array<BrowserErrorEventPayload>
    orgSlug: string
  }) => {
    await api.fetchClient.POST('/{org_slug}/browser-errors', {
      body: { events },
      params: { path: { org_slug: orgSlug } },
    })
  }
}

function defaultOrgSlug() {
  return routeOrgSlug ?? getActiveOrgSlugCookie()
}

function defaultRouteTemplate(app: BrowserErrorApp) {
  if (app === 'sidecar' || app === 'vault') {
    return `/${app}`
  }

  return sanitizeRouteTemplate(globalThis.location.pathname)
}

export function sanitizeRouteTemplate(value: string | null) {
  if (!value) {
    return null
  }

  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }

  const path = pathFromPossibleUrl(trimmed)
  const withoutQueryOrFragment = path.split('?')[0]?.split('#')[0] ?? ''
  if (
    withoutQueryOrFragment === '' ||
    !withoutQueryOrFragment.startsWith('/')
  ) {
    return null
  }

  const segments = withoutQueryOrFragment.split('/').map((segment) => {
    if (UuidPathSegmentSchema.safeParse(segment).success) {
      return '{id}'
    }

    return segment
  })
  return truncate(segments.join('/'), ROUTE_TEMPLATE_MAX_LENGTH)
}

function pathFromPossibleUrl(value: string) {
  if (!value.includes('://')) {
    return value
  }

  try {
    return new URL(value).pathname
  } catch {
    return ''
  }
}

function stripUrls(value: string) {
  return value.replace(URL_IN_TEXT, '').replace(/\s+/g, ' ').trim()
}

function truncate(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value
  }

  return value.slice(0, maxLength)
}

function truncateOptional(value: string | null | undefined, maxLength: number) {
  if (!value) {
    return null
  }

  const truncated = truncate(value.trim(), maxLength)
  if (truncated === '') {
    return null
  }

  return truncated
}

function defaultSchedule(callback: () => void, delayMs: number) {
  return Number(globalThis.setTimeout(callback, delayMs))
}

function defaultUnschedule(handle: number) {
  globalThis.clearTimeout(handle)
}

function pruneDedupe(
  recentlySentMessages: Map<string, number>,
  currentTime: number,
) {
  for (const [message, sentAt] of recentlySentMessages) {
    if (currentTime - sentAt >= DEDUPE_WINDOW_MS) {
      recentlySentMessages.delete(message)
    }
  }
}
